import { axiosPut } from '../utils/axiosPut';
import { axiosGet } from '../utils/axiosGet';
import { devError } from '../utils/devLog';
import {
  CM_PRIMARY_ID,
  ELECTION_TYPE,
  NOMINATION_STATUS,
  SE_COMMITTEE_ID,
} from '../utils/constants';

class RankingService {
  static async saveRanking({ nominationStateId, ranking, electionTypeId }) {
    try {
      const payload = {
        data: {
          ranking,
          ...(ranking === 1 &&
            electionTypeId === ELECTION_TYPE.ORDINARY && {
              councilMemberUserId: -1,
            }),
        },
      };

      const updateResult = await axiosPut(
        `nomination-states/${nominationStateId}`,
        payload,
      );

      return updateResult.data;
    } catch (error) {
      devError('Error saving ranking:', error);
      devError('Error details:', error.response?.data || error.message);
      throw error;
    }
  }

  static async removeRanking(nominationStateId) {
    try {
      const payload = {
        data: {
          ranking: null,
          councilMemberUserId: null,
        },
      };

      const updateResult = await axiosPut(
        `nomination-states/${nominationStateId}`,
        payload,
      );

      return updateResult.data;
    } catch (error) {
      devError('Error removing ranking:', error);
      devError('Error details:', error.response?.data || error.message);
      throw error;
    }
  }

  static getRankingsByCommittee(
    sectionalCommitteeId,
    nominationsArray,
    nominationStateArray,
  ) {
    try {
      let filteredNominations;

      if (sectionalCommitteeId === CM_PRIMARY_ID) {
        filteredNominations = nominationsArray.filter(
          (n) =>
            (n.attributes?.electionTypeId || n.electionTypeId) ===
              ELECTION_TYPE.CORRESPONDING_MEMBER &&
            (n.attributes?.nominationStatusTypeId ||
              n.nominationStatusTypeId) === NOMINATION_STATUS.RECOMMENDED,
        );
      } else if (sectionalCommitteeId === SE_COMMITTEE_ID) {
        filteredNominations = nominationsArray.filter(
          (n) =>
            (n.attributes?.electionTypeId || n.electionTypeId) ===
              ELECTION_TYPE.SPECIAL_ELECTION &&
            (n.attributes?.nominationStatusTypeId ||
              n.nominationStatusTypeId) === NOMINATION_STATUS.RECOMMENDED,
        );
      } else {
        filteredNominations = nominationsArray.filter(
          (n) =>
            (n.attributes?.sectionalCommitteeId || n.sectionalCommitteeId) ===
              sectionalCommitteeId &&
            (n.attributes?.nominationStatusTypeId ||
              n.nominationStatusTypeId) === NOMINATION_STATUS.RECOMMENDED,
        );
      }

      if (filteredNominations.length === 0) {
        return [];
      }

      const nominationIds = filteredNominations.map((n) => n.id);

      const nominationStates = nominationStateArray
        .filter((state) => {
          const nominationId =
            state.attributes?.nominationId || state.nominationId;
          const ranking = state.attributes?.ranking || state.ranking;
          return nominationIds.includes(nominationId) && ranking != null;
        })
        .sort((a, b) => {
          const rankA = a.attributes?.ranking || a.ranking;
          const rankB = b.attributes?.ranking || b.ranking;
          return rankA - rankB;
        });

      return nominationStates.map((state) => {
        const nominationId =
          state.attributes?.nominationId || state.nominationId;
        const nomination = filteredNominations.find(
          (n) => n.id === nominationId,
        );

        return {
          id: state.id,
          attributes: {
            nominationId,
            candidateId:
              nomination?.attributes?.candidateId || nomination?.candidateId,
            ranking: state.attributes?.ranking || state.ranking,
            sectionalCommitteeId:
              nomination?.attributes?.sectionalCommitteeId ||
              nomination?.sectionalCommitteeId,
          },
        };
      });
    } catch (error) {
      devError('Error filtering committee rankings:', error);
      throw error;
    }
  }

  static async getRankingByNomination(nominationId) {
    try {
      const response = await axiosGet('nomination-states', {
        'filters[nominationId][$eq]': nominationId,
      });

      const nominationStates = response?.data?.data || [];

      if (
        nominationStates.length > 0 &&
        nominationStates[0].attributes?.ranking
      ) {
        const state = nominationStates[0];
        return {
          id: state.id,
          attributes: {
            ranking: state.attributes.ranking,
            nominationId,
          },
        };
      }

      return null;
    } catch (error) {
      devError('Error fetching ranking:', error);
      return null;
    }
  }
}

export default RankingService;
